import { Divider, Form } from 'antd';

import { PageProps } from '@/types/PageProps';

import { DefaultRouteTabs } from '@/components/_common';
import {
  QuestReportType,
  QuestWorkOrderReportFormValues,
  QuestWorkOrderRequest,
} from '@/core/domain/questReport/Reports/WorkOrder/WorkOrder.types';
import ButtonsToGenerateReports from '@/components/Reports/ButtonsToGenerateReports';
import WorkOrdersReportForm from '@/components/Reports/MovementReports/WorkOrdersReportForm';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { DateTimeFormat } from '@/utils/moment';
import { useQuestReportWorkOrder } from '@/core/domain/questReport/Reports/WorkOrder/WorkOrder.hooks';
import { convertParams } from '@/utils/queryParams';
import { useEffect } from 'react';
import moment from 'moment';
import { useReportHandlers } from '@/hooks/useReportHandler';
import { useWorkOrderSyntheticTotalCostReport } from '@/core/domain/reports/movements/work-order-synthetic-total-cost/work-order-synthetic-total-cost.hooks';

function MovementWorkOrdersReportsPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<QuestWorkOrderReportFormValues>();
  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();

  const reportType = Form.useWatch('reportType', form);
  const isSyntheticGrouped = Form.useWatch('isSyntheticGrouped', form);

  const params = convertParams<QuestWorkOrderReportFormValues>({
    initialDate: 'string',
    finalDate: 'string',
    companiesIds: 'array-number',
    reportType: 'number',
    filterDateBy: 'number',
    situation: 'array-number',
    includeParts: 'boolean',
    includePartsProvided: 'boolean',
    includeServices: 'boolean',
    includeServicesProvided: 'boolean',
  });

  useEffect(() => {
    const { initialDate, finalDate, ...rest } = params;

    if (initialDate) form.setFieldValue('initialDate', moment(initialDate));

    if (finalDate) form.setFieldValue('finalDate', moment(finalDate));

    if (rest) {
      form.setFieldsValue(rest);
    }
  }, []);

  const generateReport = useQuestReportWorkOrder();

  const generateWorkOrderSyntheticTotalCost =
    useWorkOrderSyntheticTotalCostReport();

  const handleGenerateQuestReportWorkOrder = async (
    format: FileType,
    values: QuestWorkOrderReportFormValues,
  ) => {
    const {
      situation,
      initialDate,
      finalDate,
      companiesIds,
      estimatorsIds,
      customersIds,
      insurersIds,
      vehiclesIds,
      brandsIds,
      modelsIds,
      filterByWorkOrderType,
      ...others
    } = values;

    const obj: QuestWorkOrderRequest = {
      ...others,
      reportResponseType: format === 'CSV' ? 1 : 0,
      filterByWorkOrderType: filterByWorkOrderType.toString(),
      companiesIds: companiesIds && companiesIds.toString(),
      startDate: initialDate.format(DateTimeFormat),
      endDate: finalDate.format(DateTimeFormat),
      filterBySituation: situation && situation.toString(),
      estimatorsIds: estimatorsIds && estimatorsIds.toString(),
      customersIds: customersIds && customersIds.toString(),
      insurersIds: insurersIds && insurersIds.toString(),
      vehiclesIds: vehiclesIds && vehiclesIds.toString(),
      brandsIds: brandsIds && brandsIds.toString(),
      modelsIds: modelsIds && modelsIds.toString(),
      commissionByLiquidValue: values.commissionByLiquidValue === 'true',
    };

    return await generateReport.mutateAsync(obj);
  };

  const handleGenerateWorkOrderSyntheticTotalCost = async (
    values: QuestWorkOrderReportFormValues,
  ) => {
    const {
      situation,
      initialDate,
      finalDate,
      companiesIds,
      filterByWorkOrderType,
      filterDateBy,
      isDetailComplementsInReport,
    } = values;

    return await generateWorkOrderSyntheticTotalCost.mutateAsync({
      format: 'PDF',
      companyIds: companiesIds && companiesIds.toString(),
      startDate: initialDate.format(DateTimeFormat),
      endDate: finalDate.format(DateTimeFormat),
      filterBySituation: situation && situation.toString(),
      filterByWorkOrderType: filterByWorkOrderType.toString(),
      filterDateBy,
      isDetailComplementsInReport,
    });
  };

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const values = await form.validateFields();

      let result: BlobPart;

      if (values.isSyntheticGrouped) {
        result = await handleGenerateWorkOrderSyntheticTotalCost(values);
      } else {
        result = await handleGenerateQuestReportWorkOrder(format, values);
      }

      if (callback) {
        callback(result);
      }
    } finally {
      setFileUnloading(format);
    }
  };

  const { handleView, handlePdf, handleCsv } = useReportHandlers(
    'relatorio_orçamento',
    handleGenerateReport,
  );

  const shouldGenerateCsv =
    (reportType === QuestReportType.totalPerWorkOrder && !isSyntheticGrouped) ||
    reportType === QuestReportType.servicesAndPartsPerVehicle ||
    reportType === QuestReportType.workOrderCost;

  return (
    <DefaultRouteTabs route={route.parent} permissions={route.permissions}>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <WorkOrdersReportForm form={form} />

      <Divider />

      <ButtonsToGenerateReports
        viewReport={handleView}
        generatePdf={handlePdf}
        generateCsv={shouldGenerateCsv ? handleCsv : undefined}
        loading={fileLoading}
      />
    </DefaultRouteTabs>
  );
}

export default MovementWorkOrdersReportsPage;
